import { observable, computed, makeAutoObservable } from 'mobx';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { reduxImmutableHash } from './utils/reduxImmutableHash';
import { ReduxRawState } from './utils/ReduxRawState';

// const CombinationIO = t.interface({
//     delay: t.number,
//     ewOffered: t.boolean,
//     legs: t.array(t.interface({
//         event: t.interface({id: t.number}),
//         market: t.interface({id: t.number}),
//         price: t.interface({f: t.string, d: t.number}),
//         priceType: t.string,
//         selection: t.interface({id: t.number}),
//         type: t.string,
//     })),
//     maxStake: t.number,
//     name: t.string,
//     numLines: t.number,
//     potentialReturns: t.union([t.undefined, t.null, t.number]),
//     potentialReturnsEw: t.union([t.undefined, t.null, t.number]),
//     price: t.union([t.undefined, t.null, t.string]),
//     type: t.string,
//     visibleForBackend: t.boolean,
//     visibleForCustomer: t.boolean,
// });

const LegIO = t.interface({
    eventId: t.number,
    isSplitted: t.union([t.undefined, t.null, t.boolean]),
    marketId: t.number,
    price: t.string,
    priceType: t.string,
    selectionId: t.number,
    type: t.string,
    uid: t.string,
    uuid: t.string,
});

const BetIO = t.interface({
    affiliate: t.unknown,
    channel: t.string,
    country: t.string,
    eachWay: t.boolean,
    errors: t.union([t.undefined, t.array(t.unknown)]),
    freebet: t.boolean,
    freebetCredits: t.union([t.undefined, t.null, t.unknown]),
    freebetRemarks: t.union([t.undefined, t.null, t.unknown]),
    freeBets: t.union([t.undefined, t.null, t.array(t.string)]),
    ip: t.string,
    isExpired: t.boolean,
    isReferred: t.boolean,
    isSplitted: t.union([t.undefined, t.null, t.boolean]),
    legs: t.array(t.string),
    maxStake: t.union([t.undefined, t.null, t.number]),
    potentialReturns: t.union([t.undefined, t.null, t.number]),
    potentialReturnsAt: t.union([t.undefined, t.null, t.number]),
    price: t.union([t.undefined, t.null, t.string]),
    referredStatus: t.unknown,
    splittedTo: t.union([t.undefined, t.null, t.string]),
    stakePerLine: t.union([t.undefined, t.null, t.string, t.number]),
    type: t.string,
    uid: t.string,
});

const LegsDataIO = t.record(t.string, t.record(t.string, LegIO));
const BetsDataIO = t.record(t.string, t.array(BetIO));

const BetslipIO = t.interface({
    legs: t.unknown,
    bets: t.unknown,
    combinations: t.unknown,
});

const isBetslip = createGuard(BetslipIO);
const isLegsData = createGuard(LegsDataIO);
const isBetsData = createGuard(BetsDataIO);

type BetslipDataType = t.TypeOf<typeof BetslipIO>;
type LegsDataType = t.TypeOf<typeof LegsDataIO>;
type BetsDataType = t.TypeOf<typeof BetsDataIO>;

export class ReduxBetslipState {
    @observable private betslipData: BetslipDataType | null = null;
    @observable public bets: BetsDataType = {};
    @observable public legs: LegsDataType = {};

    public constructor(reduxStore: ReduxRawState) {
        reduxImmutableHash(
            reduxStore,
            (state: Record<string, unknown>): unknown => state.betslip,
            (data: unknown): void => {
                if (data === undefined) {
                    return;
                }

                if (isBetslip(data)) {
                    this.betslipData = data;
                    if (isBetsData(data.bets)) {
                        this.bets = data.bets;
                    }
                    if (isLegsData(data.legs)) {
                        this.legs = data.legs;
                    }
                } else {
                    console.error('ReduxBetslipState -> incorrect data', data);
                }
            }
        );
        makeAutoObservable(this);
    }

    @computed public get betslipDataForView(): BetslipDataType | null {
        return this.betslipData;
    }
}
